import { useTranslation } from '../../../../shared/i18n';
import React from 'react';
import { Button, Col, Form, Input, Radio, Row, Typography } from 'antd';
import { phoneMismatch, regionName } from './phoneCountry';
import CountryFlag from '../../../../shared/components/CountryFlag';

/**
 * One contact's fields (address-book keys): kind, company, first_name, last_name, email, phone,
 * address, address2, city, state, postal_code, country. `prefix` = form path prefix (array).
 */
export const CONTACT_KEYS = ['kind', 'company', 'first_name', 'last_name', 'email', 'phone', 'address', 'address2', 'city', 'state', 'postal_code', 'country'];

/** Any contact-shaped object (address book row / domain contact / legacy camelCase) → form values. */
export const toFormContact = (c = {}) => {
    const v = c || {};
    const company = v.company ?? v.organization ?? '';
    return {
        kind: v.kind || (company ? 'organization' : 'individual'),
        company: company || '',
        first_name: v.first_name ?? v.firstName ?? '',
        last_name: v.last_name ?? v.lastName ?? '',
        email: v.email ?? '',
        phone: v.phone ?? '',
        address: v.address ?? v.address1 ?? '',
        address2: v.address2 ?? '',
        city: v.city ?? '',
        state: v.state ?? '',
        postal_code: v.postal_code ?? v.postalCode ?? '',
        country: (v.country || '').toUpperCase(),
    };
};

/** Form values → API payload (trimmed, country upper-cased, company dropped for individuals). */
export const fromFormContact = (v = {}) => {
    const out = {};
    CONTACT_KEYS.forEach((k) => {
        const x = v[k];
        out[k] = typeof x === 'string' ? x.trim() : (x ?? '');
    });
    out.country = (out.country || '').toUpperCase();
    if (out.kind !== 'organization') out.company = '';
    return out;
};

/** One-line text for a dialing-code / country mismatch (null when they match). */
export const phoneMismatchText = (t, lang, phone, country) => {
    const m = phoneMismatch(phone, country);
    return m ? { ...m, text: t('Dialing code +{{dial}} doesn\'t match the country ({{country}})', { dial: m.dial, country: regionName(m.country, lang) }) } : null;
};

/** Grey hint (red when `error`) + one-click fix. Renders nothing when phone and country agree. */
export const PhoneCountryHint = ({ phone, country, onFix, error = false, fixOnly = false }) => {
    const { t, i18n } = useTranslation();
    const m = phoneMismatchText(t, i18n?.language, phone, country);
    if (!m) return null;
    const fix = onFix && (
        <Button type="link" size="small" style={{ padding: '0 4px', height: 'auto', fontSize: 12 }} onClick={() => onFix(m.fixed)}>
            {t('Change to +{{code}}', { code: m.expected })}
        </Button>
    );
    if (fixOnly) return fix || null;
    return (
        <Typography.Text type={error ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
            {m.text}
            {fix}
        </Typography.Text>
    );
};

/**
 * Backend field_errors {"registrant.address": msg} or {"address": msg} → antd setFields list.
 * Pass { t, lang, form } to show the phone/country mismatch in the user's language.
 */
export const fieldErrorsToForm = (fieldErrors, prefixFor, opts = {}) => {
    if (!fieldErrors || typeof fieldErrors !== 'object') return [];
    return Object.entries(fieldErrors).map(([key, msg]) => {
        const parts = key.split('.');
        const field = parts.length > 1 ? parts[parts.length - 1] : parts[0];
        const role = parts.length > 1 ? parts[0] : null;
        if (role === 'extensions') return null;
        const prefix = prefixFor(role);
        const name = [...prefix, field === 'address1' ? 'address' : field];
        let text = String(msg);
        if (field === 'phone' && opts.t && opts.form && /^电话区号/.test(text)) {
            const m = phoneMismatchText(opts.t, opts.lang, opts.form.getFieldValue([...prefix, 'phone']), opts.form.getFieldValue([...prefix, 'country']));
            if (m) text = m.text;
        }
        return { name, errors: [text] };
    }).filter(Boolean);
};

const ContactFields = ({ prefix = [], compact = false }) => {
    const { t } = useTranslation();
    const n = (k) => [...prefix, k];
    const req = [{ required: true, message: t('Required') }];
    const form = Form.useFormInstance();
    const kind = Form.useWatch(n('kind'), form);
    const phone = Form.useWatch(n('phone'), form);
    const country = Form.useWatch(n('country'), form);
    const phoneErr = form.getFieldError(n('phone')).length > 0;
    return (
        <>
            <Form.Item name={n('kind')} initialValue="individual" style={{ marginBottom: 12 }}>
                <Radio.Group optionType="button" size="small" options={[
                    { value: 'individual', label: t('Personal') },
                    { value: 'organization', label: t('Organization') },
                ]} />
            </Form.Item>
            {kind === 'organization' && (
                <Form.Item name={n('company')} label={t('Company')} rules={req}>
                    <Input placeholder="Example Ltd" />
                </Form.Item>
            )}
            <Row gutter={12}>
                <Col xs={12}>
                    <Form.Item name={n('first_name')} label={t('First name')} rules={req}>
                        <Input placeholder="San" />
                    </Form.Item>
                </Col>
                <Col xs={12}>
                    <Form.Item name={n('last_name')} label={t('Last name')} rules={req}>
                        <Input placeholder="Zhang" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item name={n('email')} label={t('Email')} rules={[...req, { type: 'email', message: t('Invalid email address format') }]}>
                        <Input placeholder="you@example.com" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name={n('phone')}
                        label={t('Phone')}
                        tooltip={t('Format: +country-code.number, e.g., +86.13800000000')}
                        rules={req}
                        extra={phoneMismatch(phone, country) ? (
                            <PhoneCountryHint phone={phone} country={country} fixOnly={phoneErr}
                                onFix={(v) => form.setFields([{ name: n('phone'), value: v, errors: [] }])} />
                        ) : undefined}
                    >
                        <Input placeholder="+86.13800000000" />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item name={n('address')} label={t('Address')} rules={req}>
                        <Input placeholder={t('Please use Pinyin or English')} />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item name={n('address2')} label={t('Address line 2')}>
                        <Input placeholder={compact ? undefined : t('Optional')} />
                    </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                    <Form.Item name={n('city')} label={t('City')} rules={req}>
                        <Input placeholder="Beijing" />
                    </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                    <Form.Item name={n('state')} label={t('Province/state')}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={12} sm={5}>
                    <Form.Item name={n('postal_code')} label={t('Postal code')} rules={req}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={12} sm={5}>
                    <Form.Item
                        name={n('country')}
                        label={t('Country')}
                        tooltip={t('Two-letter code, e.g. CN, US')}
                        normalize={(v) => (v || '').toUpperCase()}
                        rules={[...req, { pattern: /^[A-Za-z]{2}$/, message: t('e.g. CN') }]}
                    >
                        <Input maxLength={2} placeholder="CN" prefix={<span style={{ display: 'inline-flex', width: 18 }}><CountryFlag code={country} style={{ marginInlineEnd: 0 }} /></span>} />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

export default ContactFields;
